import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  IDDBDraftExportDetailResponse,
  IDDBDraftHeaderRequest,
  IDDBDraftHeaderResponse,
  IDDBDraftSubHeaderResponse,
  IDDBGoodsRequest,
  IDDBGoodsResponse,
  IDeleteDraftRequest,
  IGetDraftsResponse,
  IDDBDraftDeleteGoodsRequest,
  IDDBDraftDeleteGoodsResponse,
  ISubmitDutyDrawbackResponse,
  IDDBGoodRequest,
  IDDBGoodResponse,
  IGetDDBDraftExportsResponse,
  IDDBUpsertExportRequest,
  IDDBUpsertExportResponse,
  IDDBMetadata,
  IDDBTotalClaimResponse,
  IVerifyTransactionsResponse,
  IVerifyTransactionsRequest,
} from '../models/api';
import { StepperService } from './stepper.service';
import { tap } from 'rxjs/operators';
import { CADType, IStepState } from '../models/common';
import { dateFormat } from '@carm/shared/constants/const-carm';
import * as dayjs from 'dayjs';
import { HttpParams } from '@angular/common/http';
import { IBaseResponse } from '@carm/_utils/models';
import { ICadDraftUiContent } from '../models/declarations.model';
import { LoaderSpinnerTypes } from '@carm/_core';
import { BaseApiService } from '@carm/_core/baseapi.service';

@Injectable()
export class DutyDrawbackDraftService extends BaseApiService {
  get baseDDBDraftUrlBP(): string {
    return `${this.endPointBP}/cad/dutydrawback/draft`;
  }

  private readonly exportListSubject = new BehaviorSubject<Map<string, IGetDDBDraftExportsResponse>>(new Map<string, IGetDDBDraftExportsResponse>());

  public get onExportListChange$(): Observable<Map<string, IGetDDBDraftExportsResponse>> {
    return this.exportListSubject.asObservable();
  }

  public get exportListSnapshot(): Map<string, IGetDDBDraftExportsResponse> {
    return this.exportListSubject.getValue();
  }

  private readonly draftHeaderChangeSubject = new BehaviorSubject<IDDBDraftHeaderResponse>(null);
  //private readonly draftSubHeaderChangeSubject = new BehaviorSubject<boolean>(null);

  public get onGetDraftHeaderChange$(): Observable<IDDBDraftHeaderResponse> {
    return this.draftHeaderChangeSubject.asObservable();
  }

  public get draftHeaderSnapshot(): IDDBDraftHeaderResponse {
    return this.draftHeaderChangeSubject.getValue();
  }

  constructor(
    protected injector: Injector,
    private stepperService: StepperService
  ) {
    super(injector);
  }

  createDraft(
    draft: Partial<IDDBDraftHeaderRequest>
  ): Observable<IDDBDraftHeaderResponse> {
    const endPoint = this.baseDDBDraftUrlBP;
    return this.httpAuthPost<IDDBDraftHeaderResponse>(endPoint, draft).pipe(
      tap(draftResponse => {
        if (draftResponse) {
          this.draftHeaderChangeSubject.next({
            ...this.draftHeaderSnapshot,
            ...draftResponse,
          });
        }
      })
    );
  }

  createNonImporterDraft(
    draft: Partial<IDDBDraftHeaderRequest>
  ): Observable<IDDBDraftHeaderResponse> {
    const endPoint = this.baseDDBDraftUrlBP+"/non-importer";
    return this.httpAuthPost<IDDBDraftHeaderResponse>(endPoint, draft).pipe(
      tap(draftResponse => {
        if (draftResponse) {
          this.draftHeaderChangeSubject.next({
            ...this.draftHeaderSnapshot,
            ...draftResponse,
          });
        }
      })
    );
  }

  getDraft(draftUid: string): Observable<IDDBDraftHeaderResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}`;
    return this.httpAuthGet<IDDBDraftHeaderResponse>(endPoint).pipe(
      tap(draftResponse => {
        if (draftResponse) {
          this.draftHeaderChangeSubject.next({
            ...this.draftHeaderSnapshot,
            ...draftResponse,
          });
        }
      })
    );
  }

  getDrafts(
    minDate: Date,
    maxDate: Date,
    currentPage: number,
    pageSize: number,
    sortBy: string,
    filterField: string,
    filterValue: string
  ): Observable<IGetDraftsResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}`;
    if (minDate && maxDate) {
      const params = new HttpParams()
        .set('startDate', dayjs(minDate).format(dateFormat))
        .set('endDate', dayjs(maxDate).format(dateFormat)) // add day to include last day
        .set('pageSize', pageSize.toString())
        .set('currentPage', currentPage.toString())
        .set('sort', sortBy)
        .set('filterField', filterField)
        .set('filterValue', filterValue);
      return this.httpAuthGet<IGetDraftsResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
    } else {
      return this.httpAuthGet<IGetDraftsResponse>(endPoint);
    }
  }

  getDraftSubHeaders(
    draftUid: string,
    currentPage: number,
    pageSize: number
  ): Observable<IDDBDraftSubHeaderResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/transactions?currentPage=${currentPage}&pageSize=${pageSize}&sort=CAD_TRANS_NO_ASC`;
    return this.httpAuthGet<IDDBDraftSubHeaderResponse>(endPoint, null);
  }


  getPrecarmTotalClaimAmount(
    draftUid: string
  ): Observable<IDDBTotalClaimResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/total-claim`;
    return this.httpAuthGet<IDDBTotalClaimResponse>(endPoint, null);
  }


  updateDraft(
    draftUid: string,
    draft: IDDBDraftHeaderRequest,
    stepState: IStepState,
    isExternal?: boolean
  ): Observable<IDDBDraftHeaderResponse> {

    if (stepState) {
      let draftUiContent: ICadDraftUiContent;

      if (draft.draftUiContent) {
        draftUiContent = JSON.parse(draft.draftUiContent) as ICadDraftUiContent;

        // if json gets corrupted, or unknown state
        if(!draftUiContent?.stepStates?.some(s => s.id === stepState.id)){
          draftUiContent = {
            stepStates: this.stepperService
              .getSteps(CADType.DUTY_DRAWBACK)
              .map(step => ({ id: step.id, isComplete: false } as IStepState)),
          };
        }
      } else {
        // if draftUiContent was removed, create a new one
        draftUiContent = {
          stepStates: this.stepperService
            .getSteps(CADType.DUTY_DRAWBACK)
            .map(step => ({ id: step.id, isComplete: false } as IStepState)),
        };
      }

      const currentState = draftUiContent.stepStates.find(
        ss => ss.id === stepState.id
      );

      if (currentState) {
        currentState.isComplete = stepState.isComplete;
      } else {
        draftUiContent.stepStates.push(stepState); // a new step not part of draft?
      }

      draft.draftUiContent = JSON.stringify(draftUiContent);
    }

    const endPoint =  isExternal
            ?`${this.baseDDBDraftUrlBP}/${draftUid}/non-importer`
            :`${this.baseDDBDraftUrlBP}/${draftUid}`;
    return this.httpAuthPut<IDDBDraftHeaderResponse>(endPoint, draft);
  }

  addGoods(
    draftUid: string,
    payload: IDDBGoodsRequest
  ): Observable<IDDBGoodsResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods`;
    return this.httpAuthPost<IDDBGoodsResponse>(endPoint, payload);
  }

  getGoodByUid(
    draftUid: string,
    goodUid: string
  ): Observable<IDDBGoodResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods/${goodUid}`;
    return this.httpAuthGet<IDDBGoodResponse>(endPoint);
  }

  updateGood(
    draftUid: string,
    goodUid: string,
    good: IDDBGoodRequest
  ): Observable<IDDBGoodResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods/${goodUid}`;
    return this.httpAuthPut<IDDBGoodResponse>(endPoint, good);
  }

  deleteGoods(
    draftUid: string,
    payload: IDDBDraftDeleteGoodsRequest
  ): Observable<IDDBDraftDeleteGoodsResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods`;
    return this.httpAuthDelete<IDDBDraftDeleteGoodsResponse>(
      endPoint,
      null,
      payload
    );
  }

  getGoodsByTransactionUid(
    ddbDraftUid: string,
    transactionUid: string,
    currentPage: number,
    pageSize: number,
    sort: string = 'CAD_LN_NO_ASC'
  ): Observable<IDDBGoodsResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${ddbDraftUid}/transaction/${transactionUid}/goods?currentPage=${currentPage}&pageSize=${pageSize}&sort=${sort}`;
    return this.httpAuthGet<IDDBGoodsResponse>(endPoint, null);
  }

  getAllGoodsByTransactionUid(
    ddbDraftUid: string,
    transactionUid: string,
    sort: string
  ): Observable<IDDBGoodsResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${ddbDraftUid}/transaction/${transactionUid}/goods/all`;
    const params = new HttpParams()
      .set('sort', sort);
    return this.httpAuthGet<IDDBGoodsResponse>(endPoint, params, LoaderSpinnerTypes.AgGrid);
  }

  deleteDraft(draftUid: string, etag: string): Observable<IBaseResponse> {
    const deleteRequest: IDeleteDraftRequest = {
      etag,
    };

    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}`;
    return this.httpAuthDelete<IBaseResponse>(endPoint, null, deleteRequest);
  }

  getExports(
    ddbDraftUid: string,
    ddbGoodUid: string,
    currentPage: number,
    pageSize: number,
    sort: string
  ): Observable<IGetDDBDraftExportsResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${ddbDraftUid}/goods/${ddbGoodUid}/exports`;
    const params = new HttpParams()
      .set('pageSize', pageSize.toString())
      .set('currentPage', currentPage.toString());
    // .set('sort', sort);
    return this.httpAuthGet<IGetDDBDraftExportsResponse>(endPoint, params).pipe(
      tap(addResponse => {
        if (addResponse) {
          const snapshot = this.exportListSnapshot;
          snapshot.set(ddbGoodUid, addResponse);
          this.exportListSubject.next(snapshot);
        }
      })
    );
  }

  getExportDetails(
    draftUid: string,
    goodUid: string,
    exportUid: string
  ): Observable<IDDBDraftExportDetailResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods/${goodUid}/exports/${exportUid}`;
    return this.httpAuthGet<IDDBDraftExportDetailResponse>(endPoint);
  }

  addExport(
    draftUid: string,
    ddbGoodUid: string,
    newExport: IDDBUpsertExportRequest
  ): Observable<IDDBUpsertExportResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods/${ddbGoodUid}/exports`;
    return this.httpAuthPost<IDDBUpsertExportResponse>(
      endPoint,
      newExport
    );
  }

  updateExport(
    draftUid: string,
    ddbGoodUid: string,
    exportUid: string,
    updateExport: IDDBUpsertExportRequest
  ): Observable<IDDBUpsertExportResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods/${ddbGoodUid}/exports/${exportUid}`;
    return this.httpAuthPut<IDDBUpsertExportResponse>(endPoint, updateExport);
  }

  deleteExport(
    draftUid: string,
    ddbGoodUid: string,
    exportUid: string,
    exportEtag: string
  ): Observable<IBaseResponse> {
    const deleteRequest = {
      export: {
        etag: exportEtag,
      },
    };

    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods/${ddbGoodUid}/exports/${exportUid}`;
    return this.httpAuthDelete<IBaseResponse>(
      endPoint,
      null,
      deleteRequest
    );
  }

  validateDraft(
    draftUid: string,
    forceValidation: boolean
  ): Observable<IBaseResponse> {
    if (forceValidation) {
      const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/validate`;
      return this.httpAuthPost<IBaseResponse>(endPoint, null);
    } else {
      return of(null);
    }
  }

  submitDraft(draftUid: string): Observable<ISubmitDutyDrawbackResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/submit`;
    return this.httpAuthPost<ISubmitDutyDrawbackResponse>(endPoint, null);
  }

  getDDBMetadata(draftUid: string, goodUid: string): Observable<IDDBMetadata> {
    const endPoint = `${this.baseDDBDraftUrlBP}/${draftUid}/goods/${goodUid}/metadata`;
    return this.httpAuthGet<IDDBMetadata>(endPoint);
  }

  verifyTransactions(transactions: IVerifyTransactionsRequest): Observable<IVerifyTransactionsResponse> {
    const endPoint = `${this.baseDDBDraftUrlBP}/verify-transactions`;
    return this.httpAuthPost<IVerifyTransactionsResponse>(endPoint, transactions);
  }
}
